import React from "react";
import { Alert, Box, Button, Chip, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { realEstateApi } from "../../../api/realEstate";
import { formatApiError } from "../../../api/client";
import RealEstatePageHeader from "../../../components/dashboard/realEstate/RealEstatePageHeader";
import ObligationDialog from "../../../components/dashboard/realEstate/ObligationDialog";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";

const STATUS = { pending: "Pendiente", paid: "Pagada", overdue: "Vencida", cancelled: "Cancelada" };
const TYPE = { rent: "Alquiler", tax: "Impuesto", service: "Servicio", other: "Otro" };
const money = (value, currency = "ARS") => new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(value || 0));
const shortDate = (value) => value ? new Date(value).toLocaleDateString("es-AR") : "—";

function parseDateInput(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) return null;
  return timestamp;
}

function isInvertedDateRange(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return false;
  const from = parseDateInput(dateFrom);
  const to = parseDateInput(dateTo);
  return from !== null && to !== null && from > to;
}

export default function ProjectLeaseCollectionsPage() {
  const { projectId, canManageCollections, canReadLeases, canSendNotifications, canCreatePaymentLinks } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("estado") || "";
  const [page, setPage] = React.useState(0); const [pageSize, setPageSize] = React.useState(20);
  const [dueFrom, setDueFrom] = React.useState(""); const [dueTo, setDueTo] = React.useState("");
  const rangeInvalid = React.useMemo(() => isInvertedDateRange(dueFrom, dueTo), [dueFrom, dueTo]);
  const [data, setData] = React.useState({ obligations: [], pagination: { total: 0 } }); const [leases, setLeases] = React.useState([]);
  const [loading, setLoading] = React.useState(true); const [error, setError] = React.useState(""); const [notice, setNotice] = React.useState(""); const [copyWarning, setCopyWarning] = React.useState(""); const [manualCopyUrl, setManualCopyUrl] = React.useState(""); const [dialogOpen, setDialogOpen] = React.useState(false); const [saving, setSaving] = React.useState(false); const [actionId, setActionId] = React.useState("");
  const statusActionRef = React.useRef(new Map());
  const requestIdRef = React.useRef(0);
  const [statusActions, setStatusActions] = React.useState({});

  const load = React.useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (rangeInvalid) {
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true); setError("");
    try {
      const [obligations, leaseData] = await Promise.all([
        realEstateApi.listObligations(projectId, { page: page + 1, pageSize, status: status || undefined, dueFrom: dueFrom || undefined, dueTo: dueTo || undefined }),
        canReadLeases ? realEstateApi.listLeases(projectId, { page: 1, pageSize: 100 }) : Promise.resolve({ leases: [] }),
      ]);
      if (requestId !== requestIdRef.current) return;
      setData(obligations); setLeases(leaseData.leases || []);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(formatApiError(err, "No se pudieron cargar las cobranzas."));
    }
    finally { if (requestId === requestIdRef.current) setLoading(false); }
  }, [projectId, page, pageSize, status, dueFrom, dueTo, canReadLeases, rangeInvalid]);
  React.useEffect(() => { load(); }, [load]);
  const leaseById = React.useMemo(() => new Map(leases.map((lease) => [String(lease._id || lease.id), lease])), [leases]);
  const setStatus = (value) => { const next = new URLSearchParams(searchParams); if (value) next.set("estado", value); else next.delete("estado"); setSearchParams(next, { replace: true }); setPage(0); };
  const create = async (payload) => { setSaving(true); setError(""); try { await realEstateApi.createObligation(projectId, payload, `panel:${projectId}:${payload.leaseId}:${payload.type}:${payload.period}:${payload.dueDate}`); setDialogOpen(false); await load(); } catch (err) { setError(formatApiError(err, "No se pudo crear la obligación.")); } finally { setSaving(false); } };
  const updateStatus = async (obligation, nextStatus) => {
    const id = String(obligation?._id || obligation?.id || "");
    if (!id || statusActionRef.current.has(id)) return;

    statusActionRef.current.set(id, nextStatus);
    setStatusActions((current) => ({ ...current, [id]: nextStatus }));
    setError("");
    try {
      await realEstateApi.updateObligation(projectId, id, { status: nextStatus, ...(nextStatus === "paid" ? { paidAt: new Date().toISOString() } : {}) });
      await load();
    } catch (err) {
      setError(formatApiError(err, "No se pudo actualizar la obligación."));
    } finally {
      statusActionRef.current.delete(id);
      setStatusActions((current) => {
        if (!Object.prototype.hasOwnProperty.call(current, id)) return current;
        const next = { ...current };
        delete next[id];
        return next;
      });
    }
  };
  const resetCopyFeedback = () => { setCopyWarning(""); setManualCopyUrl(""); };
  const copyPaymentUrl = async (checkoutUrl, { successMessage = "Link copiado al portapapeles.", failureMessage = "No pudimos copiar el link automáticamente." } = {}) => {
    const url = String(checkoutUrl || "").trim();
    setNotice("");
    resetCopyFeedback();
    if (!url) {
      setCopyWarning("No hay un link de pago disponible para copiar.");
      return false;
    }
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) throw new Error("Clipboard API no disponible");
      await navigator.clipboard.writeText(url);
      setNotice(successMessage);
      return true;
    } catch (_clipboardError) {
      setCopyWarning(failureMessage);
      setManualCopyUrl(url);
      return false;
    }
  };
  const paymentLink = async (obligation, regenerate = false) => {
    const id = obligation._id || obligation.id; setActionId(`pay:${id}`); setError(""); setNotice(""); resetCopyFeedback();
    try {
      const result = await realEstateApi.createPaymentLink(projectId, id, { regenerate });
      const checkoutUrl = String(result?.checkoutUrl || "").trim();
      if (checkoutUrl) {
        const successMessage = regenerate ? "Link regenerado y copiado." : result?.reused ? "Link copiado." : "Link creado y copiado.";
        const failureMessage = regenerate
          ? "El link quedó regenerado, pero no pudimos copiarlo automáticamente. Podés copiarlo manualmente abajo."
          : result?.reused
            ? "El link sigue disponible, pero no pudimos copiarlo automáticamente. Podés copiarlo manualmente abajo."
            : "El link quedó creado, pero no pudimos copiarlo automáticamente. Podés copiarlo manualmente abajo.";
        await copyPaymentUrl(checkoutUrl, { successMessage, failureMessage });
      } else {
        setCopyWarning(regenerate
          ? "El link quedó regenerado, pero no recibimos una URL disponible para copiar."
          : "El link se procesó, pero no recibimos una URL disponible para copiar.");
      }
      await load();
    }
    catch (err) { setError(formatApiError(err, "No se pudo generar el link de pago.")); } finally { setActionId(""); }
  };
  const copyExistingPaymentLink = (checkoutUrl) => {
    setError("");
    return copyPaymentUrl(checkoutUrl, {
      successMessage: "Link copiado al portapapeles.",
      failureMessage: "No pudimos copiar el link automáticamente. Podés copiarlo manualmente abajo.",
    });
  };
  const sendNotice = async (obligation) => {
    const id = obligation._id || obligation.id; setActionId(`mail:${id}`); setError(""); setNotice("");
    try { const today = new Date().toISOString().slice(0, 10); const result = await realEstateApi.sendObligationNotification(projectId, id, {}, `panel:${id}:${today}`); setNotice(result?.skipped ? "El servicio de email está desactivado; no se registró el aviso como enviado." : "Aviso procesado sin duplicar envíos del mismo día."); await load(); }
    catch (err) { setError(formatApiError(err, "No se pudo enviar el aviso.")); } finally { setActionId(""); }
  };

  return <Stack spacing={2.5}>
    <RealEstatePageHeader title="Cobranzas" description="Obligaciones de alquiler, impuestos y servicios, con historial de estado." actionLabel={canManageCollections && leases.length ? "Nueva obligación" : ""} onAction={canManageCollections && leases.length ? () => setDialogOpen(true) : null} />
    {error ? <Alert severity="error">{error}</Alert> : null}
    {notice ? <Alert severity="success" onClose={() => setNotice("")}>{notice}</Alert> : null}
    {copyWarning ? <Alert severity="warning" onClose={() => { setCopyWarning(""); setManualCopyUrl(""); }}>
      <Stack spacing={1} sx={{ width: "100%", minWidth: 0 }}>
        <Typography variant="body2">{copyWarning}</Typography>
        {manualCopyUrl ? <TextField
          size="small"
          fullWidth
          label="Link de pago"
          value={manualCopyUrl}
          InputProps={{ readOnly: true }}
          inputProps={{ "aria-label": "Link de pago para copiar manualmente" }}
          onFocus={(event) => event.target.select()}
          helperText="Seleccioná el link y usá la acción Copiar de tu navegador o dispositivo."
        /> : null}
      </Stack>
    </Alert> : null}
    {!loading && !error && canManageCollections && leases.length === 0 ? (
      <Alert severity="info" sx={{ alignItems: "flex-start" }}>
        <Stack spacing={1.25} sx={{ width: "100%", minWidth: 0 }}>
          <Box>
            <Typography sx={{ fontWeight: 900 }}>
              {canReadLeases ? "Primero necesitás un contrato" : "Necesitás un contrato para crear cobranzas"}
            </Typography>
            <Typography variant="body2" sx={{ mt: .25 }}>
              {canReadLeases
                ? "Las cobranzas se registran sobre un contrato. Creá el primer contrato y después vas a poder generar obligaciones desde acá."
                : "No hay contratos disponibles para seleccionar y tu acceso actual no permite abrir la sección Contratos. Pedile a un administrador que cree el contrato o revise tu acceso."}
            </Typography>
          </Box>
          {canReadLeases ? (
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate(`/dashboard/proyectos/${projectId}/inmobiliaria/contratos`)}
              sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, minHeight: 44, textTransform: "none", fontWeight: 800 }}
            >
              Ir a Contratos
            </Button>
          ) : null}
        </Stack>
      </Alert>
    ) : null}
    <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField select size="small" label="Estado" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="pending">Próximas / pendientes</MenuItem>
            <MenuItem value="overdue">Vencidas</MenuItem>
            <MenuItem value="paid">Pagadas</MenuItem>
            <MenuItem value="cancelled">Canceladas</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Vence desde"
            InputLabelProps={{ shrink: true }}
            value={dueFrom}
            onChange={(event) => { setDueFrom(event.target.value); setPage(0); }}
            error={rangeInvalid}
            inputProps={{ max: dueTo || undefined }}
          />
          <TextField
            size="small"
            type="date"
            label="Vence hasta"
            InputLabelProps={{ shrink: true }}
            value={dueTo}
            onChange={(event) => { setDueTo(event.target.value); setPage(0); }}
            error={rangeInvalid}
            inputProps={{ min: dueFrom || undefined }}
          />
        </Stack>
        {rangeInvalid ? (
          <Alert severity="warning">
            La fecha “Vence desde” no puede ser posterior a “Vence hasta”.
          </Alert>
        ) : null}
      </Stack>
    </Paper>
    {rangeInvalid ? null : <TableContainer component={Paper} sx={{ borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
      {loading ? <DashboardLoadingState label="Cargando cobranzas…" minHeight={280} /> : <Table size="small"><TableHead><TableRow><TableCell>Obligación</TableCell><TableCell>Contrato / pagador</TableCell><TableCell>Vencimiento</TableCell><TableCell>Importe</TableCell><TableCell>Estado</TableCell><TableCell align="right">Acciones</TableCell></TableRow></TableHead><TableBody>
        {(data.obligations || []).map((obligation) => { const lease = leaseById.get(String(obligation.leaseId)); const obligationId = String(obligation._id || obligation.id); const statusAction = statusActions[obligationId] || ""; const statusBusy = Boolean(statusAction); return <TableRow key={obligation._id || obligation.id} hover><TableCell><Typography sx={{ fontWeight: 900 }}>{TYPE[obligation.type] || obligation.type}</Typography><Typography variant="caption" color="text.secondary">Período {obligation.period}</Typography></TableCell><TableCell>{lease?.payer?.name || `Contrato ${String(obligation.leaseId).slice(-6)}`}</TableCell><TableCell>{shortDate(obligation.dueDate)}</TableCell><TableCell sx={{ fontWeight: 800 }}>{money(obligation.amount, obligation.currency)}</TableCell><TableCell><Chip size="small" label={STATUS[obligation.status] || obligation.status} /></TableCell><TableCell align="right"><Stack direction="row" spacing={.5} justifyContent="flex-end" flexWrap="wrap">{canManageCollections && !["paid", "cancelled"].includes(obligation.status) ? <>{canCreatePaymentLinks ? <Button size="small" disabled={Boolean(actionId)} onClick={() => paymentLink(obligation, Boolean(obligation.payment?.checkoutUrl))} sx={{ textTransform: "none", fontWeight: 800 }}>{actionId === `pay:${obligation._id || obligation.id}` ? "Procesando…" : obligation.payment?.checkoutUrl ? "Regenerar link" : "Generar link"}</Button> : null}{obligation.payment?.checkoutUrl ? <Button size="small" onClick={() => copyExistingPaymentLink(obligation.payment.checkoutUrl)} sx={{ textTransform: "none" }}>Copiar link</Button> : null}{canSendNotifications ? <Button size="small" disabled={Boolean(actionId)} onClick={() => sendNotice(obligation)} sx={{ textTransform: "none" }}>{actionId === `mail:${obligation._id || obligation.id}` ? "Enviando…" : "Enviar aviso"}</Button> : null}<Button size="small" disabled={statusBusy} onClick={() => updateStatus(obligation, "paid")} sx={{ textTransform: "none", fontWeight: 800 }}>{statusAction === "paid" ? "Marcando…" : "Marcar pagada"}</Button><Button size="small" color="inherit" disabled={statusBusy} onClick={() => updateStatus(obligation, "cancelled")} sx={{ textTransform: "none" }}>{statusAction === "cancelled" ? "Cancelando…" : "Cancelar"}</Button></> : obligation.payment?.checkoutUrl ? <Button size="small" onClick={() => copyExistingPaymentLink(obligation.payment.checkoutUrl)} sx={{ textTransform: "none" }}>Copiar link</Button> : "—"}</Stack></TableCell></TableRow>; })}
        {!data.obligations?.length ? <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: "text.secondary" }}>No hay obligaciones para este filtro.</TableCell></TableRow> : null}
      </TableBody></Table>}
      <TablePagination component="div" count={data.pagination?.total || 0} page={page} onPageChange={(_event, value) => setPage(value)} rowsPerPage={pageSize} onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 20, 50, 100]} labelRowsPerPage="Filas" />
    </TableContainer>}
    <ObligationDialog open={dialogOpen} leases={leases} onClose={() => setDialogOpen(false)} onSave={create} saving={saving} />
  </Stack>;
}
